import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { replaceInquirySelection } from '@/api/inquiries';
import { getSelectionTotals, type SelectionTotals } from '@/api/library';
import {
  cachedDescendants,
  dropDescendants,
  ensureDescendants,
} from '@/features/documents/data/libraryData';
import {
  fromStored,
  mergeDrafts,
  normalize,
  parseItemKey,
  sameDraft,
  toPayload,
  type DescendantsOf,
  type ItemKey,
} from './selectionSet';

/** Changes are coalesced for this long before they are saved. */
export const SAVE_DEBOUNCE_MS = 400;
/** Delay before the one automatic retry of a failed save. */
export const SAVE_RETRY_DELAY_MS = 800;
/** Writes per drain, so repeated conflicts cannot loop forever. */
const MAX_WRITES = 6;

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** Stored ids (numbers, strings or `{id}` objects) as strings. */
export const toIds = (values: unknown): string[] =>
  Array.isArray(values)
    ? values
        .map(value =>
          value && typeof value === 'object'
            ? String((value as { id?: unknown }).id ?? '')
            : String(value ?? ''),
        )
        .filter(id => /^\d+$/.test(id))
    : [];

const foldersOf = (draft: ReadonlySet<ItemKey>) =>
  [...draft]
    .map(parseItemKey)
    .filter(ref => ref.kind === 'folder')
    .map(ref => ref.id);

/**
 * The selection of one Q&A while the source dialog is open. Every change is
 * saved through POST /llm/chat/inquiry/{id}/selection with the version it
 * was based on; it applies to the next question only. Saved is reported
 * only after the server confirmed the write.
 */
export const useSourceSelection = ({
  inquiryId,
  open,
}: {
  inquiryId: string;
  open: boolean;
}) => {
  const queryClient = useQueryClient();
  const [draft, setDraftState] = useState<Set<ItemKey>>(() => new Set());
  const [initial, setInitial] = useState<Set<ItemKey>>(() => new Set());
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [totals, setTotals] = useState<SelectionTotals | null>(null);
  // Descendants live in the query cache; this re-renders when it fills.
  const [, setRevision] = useState(0);

  const draftRef = useRef(draft);
  const confirmedRef = useRef<Set<ItemKey>>(new Set());
  const versionRef = useRef(0);
  const dirtyRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const drainRef = useRef<Promise<boolean> | null>(null);
  const mountedRef = useRef(true);
  // Writes wait until the stored selection of this Q&A is loaded.
  const readyRef = useRef(false);
  const totalsSeqRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Descendants dropped from the cache (the library changed) make folders
  // unresolved again; re-render so the dialog reads them again.
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe(event => {
        if (
          event.type === 'removed' &&
          event.query.queryKey[0] === 'library' &&
          event.query.queryKey[1] === 'descendants' &&
          mountedRef.current
        ) {
          setRevision(value => value + 1);
        }
      }),
    [queryClient],
  );

  const descendantsOf: DescendantsOf = useCallback(
    (folderId: string) => cachedDescendants(queryClient, folderId),
    [queryClient],
  );

  const resolve = useCallback(
    async (folderIds: string[]) => {
      const missing = folderIds.filter(id => !descendantsOf(id));
      if (missing.length === 0) return;
      await ensureDescendants(queryClient, missing);
      if (mountedRef.current) setRevision(value => value + 1);
    },
    [queryClient, descendantsOf],
  );

  const apply = useCallback((next: Set<ItemKey>) => {
    draftRef.current = next;
    if (mountedRef.current) setDraftState(next);
  }, []);

  const refreshTotals = useCallback(async () => {
    // Only the latest request may set the totals.
    const seq = (totalsSeqRef.current += 1);
    const response = await getSelectionTotals(inquiryId);
    if (
      response.success &&
      mountedRef.current &&
      seq === totalsSeqRef.current
    ) {
      setTotals(response.data.selection_totals ?? null);
    }
  }, [inquiryId]);

  /** The stored selection as items, with its folders resolved. */
  const storedDraft = useCallback(
    async (folderIds: string[], fileIds: string[]) => {
      await resolve(folderIds);
      return fromStored(folderIds, fileIds, descendantsOf);
    },
    [resolve, descendantsOf],
  );

  // Load the stored selection every time the dialog opens.
  useEffect(() => {
    if (!open || !inquiryId) return;
    let active = true;
    // Nothing of another Q&A, or of an earlier opening, may show or be saved.
    readyRef.current = false;
    setReady(false);
    setLoadError(false);
    setStatus('idle');
    dirtyRef.current = false;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    versionRef.current = 0;
    confirmedRef.current = new Set();
    apply(new Set());
    setInitial(new Set());
    setTotals(null);
    totalsSeqRef.current += 1;
    // Folder contents may have changed since the last opening.
    dropDescendants(queryClient);
    void (async () => {
      const response = await getSelectionTotals(inquiryId);
      if (!active) return;
      if (!response.success) {
        setLoadError(true);
        return;
      }
      const data = response.data;
      try {
        const stored = await storedDraft(
          toIds(data.folders),
          toIds(data.selection_file_ids),
        );
        if (!active) return;
        versionRef.current = Number(data.selection_version ?? 0);
        confirmedRef.current = stored;
        apply(stored);
        setInitial(stored);
        setTotals(data.selection_totals ?? null);
        readyRef.current = true;
        setReady(true);
      } catch {
        if (active) setLoadError(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, inquiryId, storedDraft, apply, queryClient]);

  const writeOnce = useCallback(
    async (
      snapshot: Set<ItemKey>,
      attempt: number,
    ): Promise<'done' | 'conflict' | 'failed'> => {
      const payload = toPayload(snapshot, descendantsOf);
      const response = await replaceInquirySelection(inquiryId, {
        base_version: versionRef.current,
        ...payload,
      });
      if (!response?.success || !response.data) {
        if (attempt === 0) {
          await new Promise(resolve =>
            setTimeout(resolve, SAVE_RETRY_DELAY_MS),
          );
          return writeOnce(snapshot, 1);
        }
        return 'failed';
      }
      const result = response.data;
      versionRef.current = Number(result.selection_version ?? 0);
      const server = await storedDraft(
        toIds(result.folder_ids),
        toIds(result.file_ids),
      );
      if (result.status === 'conflict') {
        // Saved elsewhere meanwhile: replay this dialog's changes onto it.
        const merged = mergeDrafts(
          server,
          confirmedRef.current,
          draftRef.current,
          descendantsOf,
        );
        confirmedRef.current = server;
        apply(merged);
        dirtyRef.current = true;
        return 'conflict';
      }
      confirmedRef.current = server;
      return 'done';
    },
    [inquiryId, descendantsOf, storedDraft, apply],
  );

  /** Resolves to true when everything is saved. */
  const drain = useCallback((): Promise<boolean> => {
    if (drainRef.current) return drainRef.current;
    const run = (async () => {
      let writes = 0;
      let failed = false;
      while (dirtyRef.current && writes < MAX_WRITES) {
        dirtyRef.current = false;
        const snapshot = draftRef.current;
        if (sameDraft(snapshot, confirmedRef.current)) break;
        if (mountedRef.current) setStatus('saving');
        writes += 1;
        let outcome: 'done' | 'conflict' | 'failed';
        try {
          outcome = await writeOnce(snapshot, 0);
        } catch {
          // Reading the folders of the answer failed; report it like a
          // failed save so Retry writes it again.
          outcome = 'failed';
        }
        if (outcome === 'failed') {
          dirtyRef.current = true;
          failed = true;
          break;
        }
      }
      const settled = !failed && !dirtyRef.current;
      if (mountedRef.current) setStatus(settled ? 'saved' : 'error');
      if (settled && writes > 0) void refreshTotals();
      return settled;
    })().finally(() => {
      drainRef.current = null;
    });
    drainRef.current = run;
    return run;
  }, [writeOnce, refreshTotals]);

  const schedule = useCallback(() => {
    dirtyRef.current = true;
    if (mountedRef.current) setStatus('saving');
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void drain();
    }, SAVE_DEBOUNCE_MS);
  }, [drain]);

  /**
   * Replace the selection. Folders it adds are resolved first so the set
   * can be normalized and the save carries their contents.
   */
  const commit = useCallback(
    async (next: Set<ItemKey>) => {
      if (!readyRef.current) return;
      await resolve(foldersOf(next));
      if (!readyRef.current) return;
      const normalized = normalize(next, descendantsOf);
      if (sameDraft(normalized, draftRef.current)) return;
      apply(normalized);
      schedule();
    },
    [resolve, descendantsOf, apply, schedule],
  );

  /** Save anything pending now and wait for it. */
  const flush = useCallback(async () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const settled = await drain();
    if (settled && dirtyRef.current) await drain();
    return settled;
  }, [drain]);

  const retry = useCallback(async () => {
    dirtyRef.current = true;
    await flush();
  }, [flush]);

  // Unmounting with an unsaved change still writes it.
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (dirtyRef.current) void drain();
    },
    [drain],
  );

  return {
    ready,
    loadError,
    draft,
    initial,
    status,
    totals,
    descendantsOf,
    resolve,
    commit,
    flush,
    retry,
  };
};
